import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@tellescope/design-system";
import { useCartStore, type CartItem } from "../cartStore";
import { formatShopPrice } from "../formatShopPrice";
import { findVariant, getCartItemQuantity, getMaxQuantity } from "../shopVariants";
import { BillingToggle } from "./BillingToggle";
import { QuantityStepper } from "./QuantityStepper";
import type { ShopProduct } from "../types";

interface CartItemRowProps {
  item: CartItem;
  product: ShopProduct;
}

export const CartItemRow = ({ item, product }: CartItemRowProps) => {
  const { t } = useTranslation("shop");
  const removeFromCart = useCartStore((state) => state.removeFromCart);
  const setCartItemVariant = useCartStore((state) => state.setCartItemVariant);
  const updateQuantity = useCartStore((state) => state.updateQuantity);

  const variant = findVariant(product, item.stripePriceId);
  const maxQuantity = getMaxQuantity(product, variant);

  return (
    <div className="py-4 border-b border-border last:border-0">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-muted overflow-hidden flex-shrink-0">
          {product.image && <img src={product.image} alt={product.title} className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{product.title}</p>
          {variant && <p className="text-xs text-muted-foreground">{formatShopPrice(t, variant)}</p>}
        </div>
        {maxQuantity > 1 && (
          <QuantityStepper
            value={getCartItemQuantity(item, product)}
            max={maxQuantity}
            onChange={(quantity) => updateQuantity(product.stripeProductId, quantity, maxQuantity)}
            productTitle={product.title}
          />
        )}
        <Button
          variant="ghost"
          iconOnly
          hoverLabel={t("Remove from cart")}
          onClick={() => removeFromCart(product.stripeProductId)}
          className="text-muted-foreground hover:text-destructive transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      {product.variants.length > 1 && (
        <BillingToggle
          compact
          className="mt-3"
          variants={product.variants}
          value={item.stripePriceId}
          onChange={(stripePriceId) =>
            setCartItemVariant(
              product.stripeProductId,
              stripePriceId,
              getMaxQuantity(product, findVariant(product, stripePriceId))
            )
          }
          aria-label={t("Billing frequency")}
        />
      )}
    </div>
  );
};
